import React, { useState } from 'react';
import { formatDate, formatDocument } from './contract';
import IdentityLink from './IdentityLink';
import { TRAVEL_GROUPS, formatLastPosition } from './openTravelContract';
import useOpenTravels from './useOpenTravels';
import StageList from './StageList';

const missing = 'Não informado';
const place = (point) => [point?.city, point?.province].filter(Boolean).join(' - ') || missing;
const places = (points, single) => (points?.length ? points.map(place).join(' · ') : place(single));
const columns = [
  {
    key: 'order',
    label: '#',
    width: 50,
    resizable: false,
    value: (row) => String(row.order).padStart(2, '0'),
  },
  {
    key: 'cooperplace',
    label: 'Viagem CooperPlace',
    width: 150,
    value: (row) => String(row.travelId),
    render: (row, value) => (
      <IdentityLink to={`/viagens/${row.travelId}`} aria-label={`Abrir viagem ${row.travelId}`}>
        {value}
      </IdentityLink>
    ),
  },
  {
    key: 'travel',
    label: 'Viagem Benner',
    width: 175,
    value: (row) => row.travelNumber || missing,
  },
  {
    key: 'status',
    label: 'Status',
    width: 180,
    value: (row) => row.status?.name || row.status?.code || missing,
  },
  {
    key: 'client',
    label: 'Cliente',
    width: 260,
    value: (row) =>
      `${row.client?.name || 'Cliente não informado'} · ${formatDocument(row.client?.cnpj)}`,
  },
  {
    key: 'carrier',
    label: 'Transportador',
    width: 200,
    value: (row) => row.carrier?.name || missing,
  },
  { key: 'driver', label: 'Motorista', width: 180, value: (row) => row.driver?.name || missing },
  { key: 'product', label: 'Produto', width: 160, value: (row) => row.product?.name || missing },
  {
    key: 'vehicle',
    label: 'Veículo',
    width: 200,
    value: (row) =>
      [
        row.vehicle?.plate || missing,
        ...(row.wagons || []).map((wagon) => wagon.plate || missing),
      ].join(' / '),
  },
  { key: 'origin', label: 'Origem', width: 175, value: (row) => places(row.origins, row.origin) },
  {
    key: 'destination',
    label: 'Destino',
    width: 175,
    value: (row) => places(row.destinations, row.destination),
  },
  {
    key: 'pickup',
    label: 'Coleta Agendada',
    width: 155,
    value: (row) => formatDate(row.scheduledPickupAt),
  },
  {
    key: 'delivery',
    label: 'Entrega Agendada',
    width: 165,
    value: (row) => formatDate(row.scheduledDeliveryAt),
  },
  {
    key: 'loadingStarted',
    label: 'Carregamento Iniciado',
    width: 185,
    value: (row) => formatDate(row.loadingStartedAt),
  },
  {
    key: 'loadingFinished',
    label: 'Carregamento Finalizado',
    width: 215,
    value: (row) =>
      row.origins?.length > 1
        ? row.origins
            .map((point) => `${place(point)}: ${formatDate(point.completedAt)}`)
            .join(' · ')
        : formatDate(row.loadingCompletedAt),
  },
  {
    key: 'tracker',
    label: 'Rastreador',
    width: 160,
    value: (row) => row.tracking?.tracker?.name || missing,
  },
  {
    key: 'lastPosition',
    label: 'Última Posição',
    width: 350,
    value: (row) => formatLastPosition(row.lastPosition),
  },
  { key: 'updated', label: 'Atualizado', width: 160, value: (row) => formatDate(row.updatedAt) },
];

export default function OpenTripsList({ context, onClose, rowIntegrations = {} }) {
  const [page, setPage] = useState(1);
  const { data, loading, error, refresh } = useOpenTravels({
    companyId: context.companyId,
    page,
    perPage: context.query.perPage,
    filters: context.query.filters,
  });
  const distribution = data
    ? TRAVEL_GROUPS.map(
        ({ code, label }) =>
          `${label}: ${data.groups.find((group) => group.code === code).totalCount}`,
      ).join(' · ')
    : '';
  const listContext = {
    ...context,
    query: { ...context.query, pages: { ...context.query.pages, TRIPS: page } },
  };
  const { occurrences: Occurrences, driverContact: DriverContact } = rowIntegrations;
  const integrationProps = (item) => ({
    item,
    context: { ...listContext, group: item.group },
    onChanged: refresh,
  });
  const integratedColumns = columns.map((column) => ({
    ...column,
    addon:
      column.key === 'travel' && Occurrences
        ? (row) => <Occurrences {...integrationProps(row)} />
        : column.key === 'driver' && DriverContact
          ? (row) => <DriverContact {...integrationProps(row)} />
          : undefined,
  }));
  return (
    <StageList
      definition={{
        code: 'TRIPS',
        title: 'LISTA VIAGENS',
        className: 'tc-k-open-travel-list',
        countDescription: `viagens · ${distribution}`,
        loadingLabel: 'viagens em aberto',
        emptyMessage: 'Nenhuma viagem em aberto.',
        tableLabel: 'Lista consolidada de viagens',
      }}
      columns={integratedColumns}
      context={listContext}
      queryState={{ groups: data ? { TRIPS: data } : null, loading, error }}
      onPageChange={setPage}
      onRefresh={refresh}
      onClose={onClose}
    />
  );
}
